<template>
  <SoWidget title="Direct Debit Mandate">
    <SoSkeleton v-if="current.directDebit.isFetching" class="text-h4 pb-1" />
    <button
      v-else
      class="show-details-btn d-flex justify-space-between align-center mt-6 w-100"
      @click="showDetails = !showDetails"
    >
      <SoStatus :status="mandateActiveStatus" />
      <SoIcon
        :icon="showDetails ? 'chevron-up' : 'chevron-down'"
        size="small"
        class="ml-2"
      />
    </button>
    <template #expand>
      <div v-show="showDetails" class="mx-6">
        <DirectDebitMandateDetails
          v-if="latestMandate"
          :latest-mandate="latestMandate"
        />
        <SoButton
          title="Show All Mandates"
          type="link"
          class="mt-4"
          @click="showAllMandatesModal = true"
        />
        <div>
          <SoButton
            :title="
              latestMandate?.status === 'Active'
                ? 'Change Direct Debit Details'
                : 'Set Up New Direct Debit'
            "
            type="primary"
            class="mt-4 pb-6"
            data-testid="financials-direct-debit-btn"
            data-dd-action-name="directDebitFinancialsButton"
            @click="showDirectDebitModal = true"
          />
        </div>
      </div>
    </template>
  </SoWidget>
  <AllMandatesModal v-model="showAllMandatesModal" />
  <SetUpDirectDebitModal
    ref="financials-direct-debit-modal"
    v-model="showDirectDebitModal"
    @close="showDirectDebitModal = false"
  />
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoWidget from "@/components/SoWidget.vue"
  import SoIcon from "@/components/SoIcon.vue"
  import SoSkeleton from "@/components/SoSkeleton.vue"
  import SoStatus from "@/components/SoStatus.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import AllMandatesModal from "../modals/AllMandatesModal.vue"
  import DirectDebitMandateDetails from "@/views/customer/financials/components/DirectDebitMandateDetails.vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { IDirectDebit } from "@/models/directDebit/directDebit.types"
  import { Status, StatusType } from "@/types/Status"
  import {
    getDirectDebitDefaultMandate,
    getDirectDebitSorted,
  } from "@/helpers/directDebitHelper"
  import SetUpDirectDebitModal from "@/views/customer/financials/modals/SetUpDirectDebitModal.vue"

  export default defineComponent({
    components: {
      DirectDebitMandateDetails,
      SoWidget,
      SoSkeleton,
      SoIcon,
      SoButton,
      SoStatus,
      AllMandatesModal,
      SetUpDirectDebitModal,
    },
    data() {
      return {
        showDetails: false,
        showAllMandatesModal: false,
        current: useCurrentAccountStore(),
        showDirectDebitModal: false,
      }
    },
    computed: {
      latestMandate() {
        const mandates = this.current.directDebit.data
        return getDirectDebitDefaultMandate(mandates)
      },
      mandateActiveStatus() {
        const mandateStatus = this.latestMandate?.status
        let statusType: StatusType

        switch (mandateStatus) {
          case "Active":
            statusType = "normal"
            break
          case "Failed":
            statusType = "critical"
            break
          case "Cancelled":
            statusType = "caution"
            break
          default:
            statusType = "default"
        }

        return new Status(statusType, mandateStatus ?? "No mandate")
      },
    },
    methods: {
      getLatestMandate(directDebitMandates: IDirectDebit[]) {
        return getDirectDebitSorted(directDebitMandates)
      },
    },
  })
</script>

<style scoped>
  .show-details-btn:focus-visible {
    outline: var(--theme-outline);
    border-radius: var(--theme-radius);
  }
</style>
